import { NavigateBackLink } from '@exam-management/components';
import { QuestionGroupStatus, useGetTaskQuestionGroupByIdQuery } from '@exam-management/graphql';
import {
  BaseItemSetSubQuestionSection,
  DetailSectionWrapper,
  ItemSetCreationInformationSection,
  LocationFields,
  LocationSectionWrapper,
  QuestionLayout,
} from '@exam-management/qcontent-management';
import { useQuestionCreationTaskDetails } from '@exam-management/question-creation-task-details';
import {
  ItemSetQuestionEditForm,
  useGetQBSLevelAndLevelItemsForTask,
  ItemSetCreationParagraphSection,
  useItemSetQuestionDetails,
  ItemSetSubQuestionPreview,
} from '@exam-management/question-creation-task-question-management';
import { useNavigate, useParams } from '@exam-management/router';
import {
  PaperSection,
  Loading,
  Flex,
  Text,
  FormButton,
  Box,
  PageHeader,
  SectionTitle,
  Tooltip,
  Button,
  AddRoundedIcon,
} from '@shared/components';

export default function QuestionCreationItemSetDetailPage() {
  const { questionId } = useParams(
    '/task-management/question-creation/:taskId/item-set/:questionId',
  );
  const navigate = useNavigate();

  const { task, roles } = useQuestionCreationTaskDetails();
  const qBankStructureId = task.metadata?.requirement.qBankStructure?.id ?? '';
  const {
    qbsLevelItems,
    qbsLevels,
    loading: isGettingQBSLevel,
  } = useGetQBSLevelAndLevelItemsForTask(qBankStructureId, task.id);

  const { itemSet } = useItemSetQuestionDetails();

  const { data, loading: isLoading } = useGetTaskQuestionGroupByIdQuery({
    variables: {
      questionGroupId: questionId,
      taskId: task.id,
    },
    skip: !questionId,
  });

  const isEditable = itemSet.status === QuestionGroupStatus.InDevelopment && roles.isAssignee;

  if (isGettingQBSLevel || isLoading) {
    return <Loading sx={{ height: '100%' }} />;
  }

  const levelItem = qbsLevelItems.filter((item) =>
    itemSet?.questionStructures.find((structure) => structure.refLevelItemId === item.id),
  );

  const itemSetCreation = task.metadata?.requirement.itemSetCreation?.find(
    (creation) => creation.questionGroupId === questionId,
  );

  if (isLoading || !data?.qctGetTaskQuestionGroupById) {
    return <Loading sx={{ height: '100%' }} />;
  }

  const totalCreated = itemSetCreation?.questionCreationCount ?? 0;
  const totalSize = itemSetCreation?.size ?? 0;
  const isReachMaxSubQuestions = totalCreated >= totalSize;

  return (
    <>
      <PageHeader
        title={`${data?.qctGetTaskQuestionGroupById?.name} (${data?.qctGetTaskQuestionGroupById?.refCode ?? ''})`}
        subtitleSection={
          <Flex
            sx={{
              gap: (theme) => theme.spacing(4),
              alignItems: 'center',
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <Text variant='body2'>Task ID: {task.code}</Text>
            <Text variant='body2'>{task.metadata?.requirement.qBankStructure?.name}</Text>
          </Flex>
        }
        linkAdornment={
          <NavigateBackLink
            to='/task-management/question-creation/:taskId'
            params={{ taskId: task.id }}
          />
        }
      />
      <ItemSetQuestionEditForm
        qbsLevels={qbsLevels}
        size={task.metadata?.requirement.questionCreationCount ?? 0}
        questionGroupData={data?.qctGetTaskQuestionGroupById}
      >
        <QuestionLayout
          sideContent={
            <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
              <PaperSection title='Location'>
                <LocationSectionWrapper>
                  <LocationFields qbsLevels={qbsLevels} qbsLevelItems={levelItem} />
                </LocationSectionWrapper>
              </PaperSection>
            </Flex>
          }
        >
          <Flex sx={(theme) => ({ width: '100%', flexDirection: 'column', gap: theme.spacing(6) })}>
            <PaperSection title='Question Details'>
              <DetailSectionWrapper>
                {isEditable ? (
                  <ItemSetCreationInformationSection />
                ) : (
                  <>
                    <SectionTitle title='Information' />
                    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(1) }}>
                      <Text variant='body2Semibold'>Item Set Name</Text>
                      <Box
                        sx={(theme) => ({
                          backgroundColor: theme.palette.grey[100],
                          borderRadius: theme.shape.rounded(1),
                          p: theme.spacing(2.5, 4.5),
                        })}
                      >
                        <Text>{itemSet.name}</Text>
                      </Box>
                    </Flex>
                  </>
                )}
                <ItemSetCreationParagraphSection
                  taskId={task.id}
                  questionGroupId={questionId}
                  isEditable={isEditable}
                />
                <BaseItemSetSubQuestionSection
                  slotProps={{
                    title: {
                      endAdornment: isEditable ? (
                        <Flex sx={{ gap: (theme) => theme.spacing(6), alignItems: 'center' }}>
                          <Text variant='body2'>
                            {itemSetCreation?.questionCreationCount ?? 0}/
                            {itemSetCreation?.size ?? 0} Sub Questions
                          </Text>
                          <Tooltip
                            placement='top'
                            arrow
                            title={isReachMaxSubQuestions ? 'ครบตามจำนวนที่กำหนดแล้ว' : ''}
                          >
                            <Button
                              variant='outlined'
                              disabled={isReachMaxSubQuestions}
                              onClick={() =>
                                navigate(
                                  '/task-management/question-creation/:taskId/item-set/:questionId/create',
                                  {
                                    params: {
                                      taskId: task.id,
                                      questionId,
                                    },
                                  },
                                )
                              }
                              sx={(theme) => ({
                                '&.Mui-disabled': {
                                  pointerEvents: 'auto',
                                  bgcolor: theme.palette.grey[100],
                                  ':hover': {
                                    borderColor: theme.palette.grey['300'],
                                  },
                                },
                              })}
                              startIcon={<AddRoundedIcon />}
                            >
                              Add Sub Question
                            </Button>
                          </Tooltip>
                        </Flex>
                      ) : null,
                    },
                  }}
                >
                  <ItemSetSubQuestionPreview taskId={task.id} />
                </BaseItemSetSubQuestionSection>
              </DetailSectionWrapper>
            </PaperSection>
            {isEditable ? (
              <Box>
                <FormButton label='Save' />
              </Box>
            ) : null}
          </Flex>
        </QuestionLayout>
      </ItemSetQuestionEditForm>
    </>
  );
}
